<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.time-off-balances-edit-dialog(
    :title="title"
    append-to-body
    :show="show"
    @close="close"
  )
    .alert.alert-danger(v-if="baseError") {{ baseError }}
    ValidationProvider(
      v-slot="{ errors }"
      rules="required"
      name="time_off_policy_id"
      :model-value="time_off_policy_id"
    )
      .form-group(:class="{ 'has-error': errors[0] }")
        label.control-label(for="time_off_policy_id") Time Off Policy*
        AppSelect(
          ref="policySelect"
          v-model="policyModel"
          name="time_off_policy_id"
          :select2-options="select2Options"
          :options="timeOffPoliciesOptions"
        )
        span.help-block {{ errors[0] }}
    .form-group(v-if="showAnnual")
      AppCheckbox(v-model="apply_accruals") Apply the starting annual accrual amount
    ValidationProvider(
      v-slot="{ errors }"
      rules="required"
      name="modify_balance"
      :model-value="modify_balance"
    )
      .form-group(:class="{ 'has-error': errors[0] }")
        label.control-label Modify Balance*
        AppSelect(
          ref="modify_balance_select"
          v-model="modify_balance"
          :select2-options="select2Options"
          :options="modifyBalanceOptions"
        )
        span.help-block {{ errors[0] }}
    ValidationProvider(
      v-slot="{ errors }"
      :rules="amountValidationRules"
      name="amount"
      :model-value="amount"
    )
      .form-group(:class="{ 'has-error': errors[0] }")
        label.control-label(for="amount") Amount*
        AppInput(
          v-model="amount"
          name="amount"
          placeholder="Enter in hours"
        )
        span.help-block {{ errors[0] }}
        span.amount-info(v-if="modify_balance == 'add'") Negative amounts subtract from the balance
    ValidationProvider(
      v-slot="{ errors }"
      rules="required"
      name="reason"
      :model-value="reason"
    )
      .form-group.text(:class="{ 'has-error': errors[0] }")
        label.control-label(for="reason") Reason*
        textarea.form-control(
          v-model="reason"
          name="reason"
          rows="4"
          placeholder="Enter the reason you are adjusting the balance"
        )
        span.help-block {{ errors[0] }}
    template(#footer)
      button.btn.btn-default(@click="close") Cancel
      button.btn.btn-primary(
        :disabled="saving"
        @click="save"
      )
        | {{ saveButtonLabel }}
</template>
<script>
import { mapGetters } from 'vuex';
import { extend } from '~/utils/vee_validate_compat';

extend('year', {
  validate: (value) => {
    return value < 8760 && value > -8760;
  },
  message: 'must be less than 1 year.',
});

export default {
  name: 'TimeOffBalancesEditDialog',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Adjust balances',
    },
    userIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      errorMessage: 'There was an error when updating time off balances',
      successMessage: 'Time off balances updated',
      select2Options: {
        allowClear: false,
        multiple: false,
        width: '100%',
      },
      saving: false,
      time_off_policy_id: '',
      apply_accruals: true,
      modify_balance: 'add',
      isAnnual: false,
      amount: '',
      reason: '',
      baseError: null,
    };
  },
  computed: {
    ...mapGetters(['editableTimeOffPolicies']),
    policyModel: {
      get() {
        return this.time_off_policy_id;
      },
      set(value) {
        this.isAnnual = !!this.$refs.policySelect.selectedData()[0].annual;
        this.time_off_policy_id = value;
      },
    },
    timeOffPoliciesOptions() {
      return this.editableTimeOffPolicies.map((policy) => ({ id: policy.id, text: policy.name, annual: policy.annual }));
    },
    modifyBalanceOptions() {
      return [
        { id: 'add', text: 'Add to balance' },
        { id: 'replace', text: 'Replace balance' },
      ];
    },
    amountValidationRules() {
      let rules = 'required|';

      if (this.modify_balance === 'add') {
        rules += 'integer';
      } else {
        rules += 'numeric';
      }
      rules += '|year';

      return rules;
    },
    timeOffBalanceParams() {
      return {
        reason: this.reason,
        apply_accruals: this.apply_accruals,
        balance_adjustments: [
          {
            time_off_policy_id: this.time_off_policy_id,
            user_ids: this.userIds,
            amount: this.amount * 60 * 60,
            modify_balance: this.modify_balance,
          },
        ],
      };
    },
    showAnnual() {
      return this.isAnnual && this.modify_balance === 'add';
    },
    saveButtonLabel() {
      return this.saving ? 'Saving...' : 'Save';
    },
  },
  created() {
    this.reset();
  },
  mounted() {
    this.$refs.dialogObserver.reset();
  },
  methods: {
    close() {
      this.reset();
      this.$refs.dialogObserver.reset();
      this.$emit('close');
    },
    async save() {
      this.baseError = null;
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid && confirm('This will update the balances of these members, are you sure?')) {
        const success = this.success;
        const error = this.error;
        const fin = () => {
          this.saving = false;
        };

        this.saving = true;
        this.$store.dispatch('updateBalances', { params: this.timeOffBalanceParams }).then(success, error).finally(fin);
      }
    },
    success(response) {
      this.$appMessage(this.successMessage);
      this.$store.dispatch('loadData');
      this.close();
    },
    error(response) {
      if (response.responseJSON) {
        if (response.responseJSON.alert) {
          this.$appMessage(response.responseJSON.alert);
        }
        if (response.responseJSON.errors && Object.keys(response.responseJSON.errors).length) {
          this.$refs.dialogObserver.setErrors(response.responseJSON.errors);
        } else {
          this.baseError = response.responseJSON.error_message;
        }
      } else {
        this.$appMessage(this.errorMessage);
      }
    },
    reset() {
      // Policies may be empty
      if (this.timeOffPoliciesOptions[0]) {
        this.time_off_policy_id = this.timeOffPoliciesOptions[0].id;
        this.isAnnual = this.timeOffPoliciesOptions[0].annual;
      }
      this.apply_accruals = true;
      this.modify_balance = this.modifyBalanceOptions[0].id;
      this.amount = '';
      this.reason = '';
      this.baseError = null;
      this.saving = false;
    },
  },
};
</script>
